<template>
  <div class="wrapper wrapper-content animated fadeInRight">

    <div class="row m-b-lg">
      <div class="col-sm-6">
        <account-credit></account-credit>
      </div>
      <div class="col-sm-6 text-right" v-if="canAddCreditNotes">
        <a href="#modal-add-credit-note" class="btn btn-info btn-sm m-t-sm" data-toggle="modal" data-target="#modal-credit-note">
          <i class="fa fa-plus"></i> Add Credit
        </a>
      </div>
    </div>

    <div class="ibox">
      <div class="ibox-content">

        <ul class="list-detail list-detail-credit-note">
          <li v-for="creditNote in creditNotes">
            <div class="row">
              <div class="col-md-6">
                <p class="truncate">{{ creditNote.reference }}</p>
              </div>
              <div class="col-xs-4 col-md-2">
                <p class="small">{{ creditNote.date }}</p>
              </div>
              <div class="col-xs-4 col-md-2">
                <p class="small">CN#{{ creditNote.code }}</p>
              </div>
              <div class="col-xs-4 col-md-2">
                <p class="strong text-right">{{ creditNote.total }}</p>
              </div>
            </div>
            <ul class="details">
              <li v-for="item in creditNote.itemsDecorated">
                {{ item.description }} <span class="pull-right">{{ item.currencyAmount }}</span>
              </li>
            </ul>
          </li>
        </ul>

        <div class="text-center text-muted m-t-md m-b-sm" v-show="haveNoCreditNotes">
          <strong>There are no credit notes.</strong>
        </div>
      </div>
    </div>

    <div class="modal fade" id="modal-credit-note" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            Add Credit Note
          </div>
          <form>
            <div class="modal-body">
              <div class="row m-b-lg">
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="control-label">Client Name</label>

                    <input type="text" class="form-control form-control-decorate" :value="user.profile.full_name" disabled>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="control-label">Reference Text</label>

                    <input
                      type="text"
                      class="form-control form-control-decorate"
                      placeholder="Reason for credit note..."
                      v-model="creditNote.reference">
                  </div>
                </div>
              </div>

              <h3>Note Items</h3>

              <div class="form-horizontal">

                <div class="form-group" v-for="(item, index) in creditNote.items">
                  <div class="col-xs-12 col-sm-7">
                    <label class="sr-only">Description</label>
                    <input
                      type="text"
                      class="form-control form-control-decorate"
                      :value="item.description"
                      placeholder="Description..."
                      @input="updateCreditNoteItemDescription(index, $event)">
                  </div>
                  <div class="col-xs-9 col-sm-3">
                    <label class="sr-only">Amount</label>
                    <div class="input-group">
                      <span class="input-group-addon"><i class="fa fa-usd"></i></span>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="0.00"
                        :value="item.amount"
                        @input="updateCreditNoteItemAmount(index, $event)">
                    </div>
                  </div>
                  <div class="col-xs-3 col-sm-2">
                    <a class="btn btn-default btn-danger btn-circle pull-right" @click="removeCreditNoteItem(index)"><i class="fa fa-fw fa-minus"></i></a>
                  </div>
                </div>

                <div class="form-group">
                  <div class="col-sm-offset-7 col-sm-3">
                    <label class="sr-only">Amount</label>
                    <div class="input-group">
                      <span class="input-group-addon"><i class="fa fa-usd"></i></span>
                      <input type="text" class="form-control" :value="creditNoteItemTotal" disabled />
                    </div>
                  </div>
                </div>
              </div>

              <a class="btn btn-xs btn-primary" @click="addCreditNoteItem"><i class="fa fa-plus"></i> Add Line</a>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-sm btn-default" data-dismiss="modal">Cancel</button>
              <ladda-button :on-click="handleSaveCreditNote">Save</ladda-button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import { LaddaButton } from 'components/forms'
  import { existingUser } from 'mixins/user'
  import { sumBy, cloneDeep, remove, noop } from 'lodash'
  import { notify } from 'utils'
  import AccountCredit from './AccountCredit'
  import accounting from 'accounting'
  import $ from 'jquery'

  export default {
    mixins: [existingUser],

    components: {
      LaddaButton,
      AccountCredit
    },

    data () {
      return {
        creditNote: null
      }
    },

    created () {
      const redirectToAccountSettings = () => (this.$router.push({ name: 'account.settings' }))

      // If the user is not a client or carrier then kick them out! Only clients
      // and carriers can see credit notes. Unless, of course, they are trying
      // to view another users credit notes.
      if (this.$route.name === 'account.credit-notes' && !this.$user.isClientOrCarrier) {
        redirectToAccountSettings()

        return
      }

      const fetchUserCreditNotes = () => {
        return this.getCreditNotes(this.user.id)
      }

      this.resetCreditNote()
      this.attemptToLoadExistingUser().then(fetchUserCreditNotes).catch(noop)
    },

    mounted () {
      $('#modal-credit-note').on('show.bs.modal', this.resetCreditNote)
    },

    beforeDestroy () {
      $('#modal-credit-note').off('show.bs.modal', this.resetCreditNote)
    },

    computed: {
      ...mapGetters('user/creditNote', { creditNotes: 'creditNotesDecorated' }),

      /**
       * Determine if we can add a credit note.
       *
       * @return boolean
       */
      canAddCreditNotes () {
        return this.$user.isAdmin
      },

      /**
       * Running total of credit note items.
       *
       * @return float
       */
      creditNoteItemTotal () {
        return accounting.formatMoney(sumBy(this.creditNote.items, item => (parseFloat(item.amount) || 0)), {
          symbol: ''
        })
      },

      /**
       * Determine if we have no credit notes.
       *
       * @return boolean
       */
      haveNoCreditNotes () {
        return this.creditNotes.length === 0
      }
    },

    methods: {
      ...mapActions('user/creditNote', ['saveCreditNote', 'getCreditNotes', 'getAccountCredit']),

      /**
       * Reset the local credit note state.
       *
       * @return void
       */
      resetCreditNote () {
        this.creditNote = {
          reference: null,
          items: [
            {
              description: null,
              amount: null
            }
          ]
        }
      },

      /**
       * Remove a credit note item. Adds a new item if there are no items
       * in the array.
       *
       * @param integer index
       *
       * @return void
       */
      removeCreditNoteItem (index) {
        this.creditNote.items.splice(index, 1)

        if (this.creditNote.items.length === 0) {
          this.addCreditNoteItem()
        }
      },

      /**
       * Add a blank credit note item.
       *
       * @return void
       */
      addCreditNoteItem () {
        this.creditNote.items.push({ description: null, amount: null })
      },

      /**
       * Update a credit note item description.
       *
       * @param integer index
       * @param object target
       *
       * @return void
       */
      updateCreditNoteItemDescription (index, { target }) {
        this.creditNote.items[index].description = target.value
      },

      /**
       * Update a credit note item amount.
       *
       * @param integer index
       * @param object target
       *
       * @return void
       */
      updateCreditNoteItemAmount (index, { target }) {
        this.creditNote.items[index].amount = target.value
      },

      /**
       * Handle saving a credit note.
       *
       * @return Promise
       */
      handleSaveCreditNote () {
        const creditNoteSaved = () => {
          notify.success('Credit note has been saved.')

          $('#modal-credit-note').modal('hide')
        }

        const failedSavingCreditNote = () => {
          notify.error('There was a problem saving the credit note. Review the credit note details and try again.')
        }

        const updateAccountCredit = () => (this.getAccountCredit(this.user.id))

        // Clone the credit note so we can remove blank items from it before attempting to save it.
        // This makes adding them easier if you've added a bunch of lines but only fill out a few.
        let payload = cloneDeep(this.creditNote)

        remove(payload.items, item => item.description === null && item.amount === null)

        return this.saveCreditNote({ user: this.user.id, payload }).then(updateAccountCredit).then(creditNoteSaved).catch(failedSavingCreditNote)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// CreditNotes.vue?1fbb6dae